import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { TAGS_KEYS } from "@/other/constants/TagsKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { TagValue } from "@/bryntum-lib/models/TagValue";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Combobox } from "@/modern/components/ui/combobox";
import { UserAvatar, BoardUser } from "@/modern/components/user-avatar";
import { Button } from "@/modern/components/ui/button";
import { UserPlus } from "lucide-react";

const TaskStatusDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [statuses, setStatuses] = useState<TagValue[]>([]);
  const [boardUsers, setBoardUsers] = useState<BoardUser[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.taskTagValuesStore,
  })

  const { subscribe: subscribeBoardUsers } = useSwivuStore({
    storeId: STORE_KEYS.boardUserStore,
  })

  const loadStatuses = () => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    const statusRecords = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.status, true) || [];
    setStatuses(() => statusRecords);
  }

  const loadBoardUsers = () => {
    if (!project) return;
    const boardUsersStore = project.boardUsersStore;
    if (!boardUsersStore) return;
    const userRecords = (boardUsersStore.query(() => true, true) || []) as unknown as BoardUser[];
    setBoardUsers(userRecords);
  };

  useEffect(() => {
    loadStatuses();
    return subscribe(() => {
      loadStatuses();
    })
  }, [subscribe]);

  useEffect(() => {
    loadBoardUsers();
    return subscribeBoardUsers(() => {
      loadBoardUsers();
    });
  }, [subscribeBoardUsers]);

  const isDuplicateStatusName = (name: string, excludeStatusId?: string | number) => {
    if (!project) return false;
    const tagValueStore = project.taskTagValues;
    const allStatuses = tagValueStore.query((record: TagValue) => record.tagSet === TAGS_KEYS.status, true) || [];
    return allStatuses.some(s => {
      if (excludeStatusId !== undefined && s.id === excludeStatusId) {
        return false;
      }
      return s.tagValue.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateStatusName(value)) {
      toast.error('Status name already exists');
      return;
    }

    if (!project) return;
    const tagValueStore = project.taskTagValues;
    const newStatus = tagValueStore.createRecord({
      tagSet: TAGS_KEYS.status,
      tagValue: value,
    });
    tagValueStore.add(newStatus);
  }

  const handleReorderItems = (movedItem: TagValue, beforeItem: TagValue | null) => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    // @ts-expect-error - tagValue is a TagValue model
    tagValueStore.move(movedItem, beforeItem);
  }

  const handleUpdateStatusName = (status: TagValue, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicateStatusName(newName, status.id)) {
      toast.error('Status name already exists');
      return false;
    }

    status.tagValue = newName;
    return true;
  }

  const handleDeleteStatus = (status: TagValue) => {
    if (!project) return;
    const tagValueStore = project.taskTagValues;
    // @ts-expect-error - status is a TagValue model
    tagValueStore.remove(status);
  }

  const handleNotifyUsersChange = (status: TagValue, newValue: string | number | string[] | number[] | null) => {
    const userIds = Array.isArray(newValue) ? newValue.map(v => Number(v)) : [];
    status.notifyees = userIds;
  }

  const userOptions = useMemo(() => {
    return boardUsers.map(user => ({
      label: user.name,
      value: user.id.toString(),
    }));
  }, [boardUsers]);

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<TagValue>
        items={statuses}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["tagValue"],
          placeholder: "Search statuses...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Header>
          <SwivuList.HeaderHandleSpace />
          <SwivuList.HeaderItem flex>
            Status Name
          </SwivuList.HeaderItem>
          <SwivuList.HeaderItem flex>
            Notify Users
          </SwivuList.HeaderItem>
          <div className="w-[2.5rem]" />
        </SwivuList.Header>

        <SwivuList.Content<TagValue> emptyMessage="No statuses yet. Create your first status!">
          {(status: TagValue) => {
            const selectedUsers = boardUsers.filter(user =>
              (status.notifyees || []).includes(user.id as number)
            );

            return (
              <SwivuList.Item value={status.id as string | number}>
                {/* Drag Handle */}
                <SwivuList.ItemHandle disabled={!canEdit} value={status.id as string | number} />

                {/* Status Name */}
                <div className="flex-1 px-4 py-2">
                  <SwivuList.TextField
                    item={status}
                    value={status.tagValue}
                    onUpdate={handleUpdateStatusName}
                    placeholder="Enter status name"
                    disabled={!canEdit}
                    className="flex-1"
                  />
                </div>

                {/* Notify Users */}
                <div className="flex items-center flex-1 gap-2">
                  {/* Show first 5 selected users with avatars */}
                  {selectedUsers.slice(0, 5).map(user => (
                    <UserAvatar
                      key={user.id}
                      user={user}
                      showTooltip={true}
                      showRemove={canEdit}
                      onRemove={() => {
                        const newValue = (status.notifyees || []).filter((id: number) => id !== (user.id as number));
                        handleNotifyUsersChange(status, newValue.map((v: number) => v.toString()));
                      }}
                      avatarClassName="ring-0"
                    />
                  ))}

                  {/* Show count if more than 5 users */}
                  {selectedUsers.length > 5 && (
                    <div className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                      <span className="text-xs text-muted-foreground font-medium">
                        +{selectedUsers.length - 5}
                      </span>
                    </div>
                  )}

                  <Combobox
                    options={userOptions}
                    value={(status.notifyees || []).map((v: number) => v.toString())}
                    onValueChange={(value) => handleNotifyUsersChange(status, value as string[])}
                    placeholder="Select users"
                    searchPlaceholder="Search users..."
                    disabled={!canEdit}
                    multiSelect={true}
                    size="sm"
                    popoverContentClassName="w-max"
                    className="w-auto"
                    renderer={() => (
                      <>
                        {canEdit && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="size-8"
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        )}
                      </>
                    )}
                  />
                </div>

                {/* Actions */}
                <SwivuList.ItemActions
                  disabled={!canEdit}
                  onDelete={() => handleDeleteStatus(status)}
                />
              </SwivuList.Item>
            );
          }}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new status..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default TaskStatusDetail;
